import styles from "./MoneyBar.module.scss";

interface MoneyBarProps {
  /** Total money-in amount (in cents, positive). */
  moneyIn: number;
  /** Total money-out amount (in cents, positive absolute value). */
  moneyOut: number;
  /** Accessible label for screen readers (should be localized by the caller). */
  ariaLabel: string;
  /** Optional testId for the wrapper element. */
  testId?: string;
}

/**
 * Proportional horizontal bar showing the ratio of money-in (green)
 * to money-out (gray). If both values are zero the track is empty.
 *
 * Uses Math.floor for the in-segment so that extremely small out-segments
 * (e.g. $1.00 in / $0.01 out) are never rounded away to 0%.
 */
const MoneyBar = ({ moneyIn, moneyOut, ariaLabel, testId }: MoneyBarProps) => {
  const total = moneyIn + moneyOut;

  // Nothing to render when there is no money movement at all.
  if (total === 0) return null;

  // Use Math.floor for inPercent so the remainder always goes to outPercent,
  // preventing a rounding artifact that could hide a nonzero out-segment.
  const inPercent = Math.floor((moneyIn / total) * 100);
  const outPercent = 100 - inPercent;

  return (
    <div
      className={styles.track}
      data-testid={testId}
      role="img"
      aria-label={ariaLabel}
    >
      {moneyIn > 0 && (
        <div
          className={styles.segmentIn}
          style={{ width: `${moneyOut > 0 ? inPercent : 100}%` }}
          data-testid="money-bar-in"
        />
      )}
      {moneyOut > 0 && (
        <div
          className={styles.segmentOut}
          style={{ width: `${moneyIn > 0 ? outPercent : 100}%` }}
          data-testid="money-bar-out"
        />
      )}
    </div>
  );
};

export default MoneyBar;
